<template>
  <div
    ref="rootRef"
    class="ui-datepicker"
    :class="[
      `ui-datepicker--${size}`,
      {
        'ui-datepicker--opened': isOpen,
        'ui-datepicker--range': isRange,
        'ui-datepicker--disabled': disabled,
        'ui-datepicker--error': error,
        'ui-datepicker--has-value': isRange ? startDate || endDate : selectedDate,
      },
    ]"
  >
    <label v-if="label" class="ui-datepicker__label" :for="id">
      {{ label }}
      <span v-if="required" class="ui-datepicker__required">*</span>
    </label>

    <div class="ui-datepicker__wrapper" ref="inputRef">
      <!-- 단일 날짜 선택 -->
      <div v-if="!isRange" class="ui-datepicker__input" @click="openCalendar">
        <i class="icon icon-calendar icon-md ui-datepicker__icon"></i>
        <div class="ui-datepicker__text">
          <span v-if="selectedDate" class="ui-datepicker__selected-text">{{ formatDate(selectedDate) }}</span>
          <span v-else class="ui-datepicker__placeholder">{{ placeholder || t('common.datePicker.placeholder') }}</span>
        </div>
        <i v-if="selectedDate && clearable" class="icon icon-x-circle icon-md ui-datepicker__clear" @click.stop="clearDate"></i>
      </div>

      <!-- 범위 날짜 선택 -->
      <div v-if="isRange && !splitRange" class="ui-datepicker__range-input" @click="openCalendar">
        <i class="icon icon-calendar icon-md ui-datepicker__icon"></i>
        <div class="ui-datepicker__range-text">
          <span v-if="startDate" class="ui-datepicker__selected-text">{{ formatDate(startDate) }}</span>
          <span v-else class="ui-datepicker__placeholder">{{ startPlaceholder || t('common.datePicker.startDate') }}</span>
          <i v-if="startDate && clearable" class="icon icon-x-circle icon-lg" @click.stop="clearStartDate"></i>
        </div>
        <div class="ui-datepicker__range-separator">-</div>
        <div class="ui-datepicker__range-text">
          <span v-if="endDate" class="ui-datepicker__selected-text">{{ formatDate(endDate) }}</span>
          <span v-else class="ui-datepicker__placeholder">{{ endPlaceholder || t('common.datePicker.endDate') }}</span>
          <i v-if="endDate && clearable" class="icon icon-x-circle icon-lg" @click.stop="clearEndDate"></i>
        </div>
      </div>

      <!-- 범위 날짜 선택 => 시작일/종료일 각각 클릭 가능 -->
      <div v-if="isRange && splitRange" class="ui-datepicker__range-input">
        <i class="icon icon-calendar icon-md ui-datepicker__icon"></i>

        <div class="ui-datepicker__range-text" @click.stop="openCalendar('start')">
          <span v-if="startDate" class="ui-datepicker__selected-text">{{ formatDate(startDate) }}</span>
          <span v-else class="ui-datepicker__placeholder">{{ startPlaceholder || t('common.datePicker.startDate') }}</span>
          <i v-if="startDate && clearable" class="icon icon-x-circle icon-lg" @click.stop="clearStartDate"></i>
        </div>

        <div class="ui-datepicker__range-separator">-</div>

        <div class="ui-datepicker__range-text" @click.stop="openCalendar('end')">
          <span v-if="endDate" class="ui-datepicker__selected-text">{{ formatDate(endDate) }}</span>
          <span v-else class="ui-datepicker__placeholder">{{ endPlaceholder || t('common.datePicker.endDate') }}</span>
          <i v-if="endDate && clearable" class="icon icon-x-circle icon-lg" @click.stop="clearEndDate"></i>
        </div>
      </div>
    </div>

    <!-- TODO 0917 캘린더 (fixed 포지션으로 body에 렌더링) -->
    <teleport to="body">
      <div v-if="isOpen" ref="calendarRef" class="ui-datepicker__calendar ui-datepicker__calendar--fixed" :style="calendarStyle">
        <!-- 월 선택 모드  -->
        <div v-if="viewMode === 'months'">
          <div class="ui-datepicker__header">
            <button class="ui-datepicker__nav-btn" @click="prevYear" type="button">
              <i class="icon icon-chevron-left icon-md"></i>
            </button>

            <div class="ui-datepicker__title">{{ yearTitle }}</div>

            <button class="ui-datepicker__nav-btn" @click="nextYear" type="button">
              <i class="icon icon-chevron-right icon-md"></i>
            </button>
          </div>

          <div class="ui-datepicker__months">
            <div
              v-for="(month, index) in months"
              :key="month"
              class="ui-datepicker__month"
              :class="{
                'ui-datepicker__month--selected': isMonthSelected(index),
                'ui-datepicker__month--disabled': isMonthDisabled(index),
              }"
              @click="!isMonthDisabled(index) && selectMonth(index)"
            >
              {{ month }}
            </div>
          </div>
        </div>

        <!-- 날짜 선택 모드 -->
        <div v-if="viewMode === 'days'">
          <div class="ui-datepicker__header">
            <button class="ui-datepicker__nav-btn" @click="prevMonth" type="button">
              <i class="icon icon-chevron-left icon-md"></i>
            </button>

            <div class="ui-datepicker__selectors">
              <!-- 년도 선택 드롭다운 (먼저 위치) -->
              <div class="ui-datepicker__dropdown">
                <div class="ui-datepicker__selector" @click.stop="toggleYearDropdown">
                  {{ currentYear }}
                  <i class="icon icon-chevron-down icon-sm"></i>
                </div>
                <div v-if="yearDropdownOpen" ref="yearDropdownListRef" class="ui-datepicker__dropdown-list" @click.stop>
                  <div
                    v-for="year in yearRange"
                    :key="year"
                    class="ui-datepicker__dropdown-item"
                    :class="{
                      'ui-datepicker__dropdown-item--selected': currentYear === year,
                    }"
                    @click="selectYear(year)"
                  >
                    {{ year }}
                  </div>
                </div>
              </div>

              <!-- 월 선택 드롭다운 (나중 위치) -->
              <div class="ui-datepicker__dropdown">
                <div class="ui-datepicker__selector" @click.stop="toggleMonthDropdown">
                  {{ currentMonthName }}
                  <i class="icon icon-chevron-down icon-sm"></i>
                </div>
                <div v-if="monthDropdownOpen" class="ui-datepicker__dropdown-list" @click.stop>
                  <div
                    v-for="(month, index) in months"
                    :key="month"
                    class="ui-datepicker__dropdown-item"
                    :class="{
                      'ui-datepicker__dropdown-item--selected': currentMonth === index,
                    }"
                    @click="selectMonth(index)"
                  >
                    {{ month }}
                  </div>
                </div>
              </div>
            </div>

            <button class="ui-datepicker__nav-btn" @click="nextMonth" type="button">
              <i class="icon icon-chevron-right icon-md"></i>
            </button>
          </div>

          <div class="ui-datepicker__weekdays">
            <div v-for="day in weekdays" :key="day" class="ui-datepicker__weekday">
              {{ day }}
            </div>
          </div>

          <div class="ui-datepicker__days">
            <div
              v-for="(day, index) in calendarDays"
              :key="index"
              class="ui-datepicker__day"
              :class="{
                'ui-datepicker__day--other-month': !day.currentMonth,
                'ui-datepicker__day--today': day.isToday,
                'ui-datepicker__day--selected': isSelected(day.date),
                'ui-datepicker__day--in-range': isInRange(day.date),
                'ui-datepicker__day--disabled': isDateDisabled(day.date),
              }"
              @click="!isDateDisabled(day.date) && selectDate(day)"
            >
              {{ day.day }}
            </div>
          </div>
        </div>

        <div v-if="isRange && (viewMode === 'days' || viewMode === 'months')" class="ui-datepicker__actions">
          <button class="ui-datepicker__action-btn ui-datepicker__action-btn--cancel" @click="cancelSelection">
            {{ t('common.datePicker.cancel') }}
          </button>
          <button
            class="ui-datepicker__action-btn ui-datepicker__action-btn--apply"
            @click="applySelection"
            :disabled="viewMode === 'months' && !(startDate && endDate)"
          >
            {{ t('common.datePicker.apply') }}
          </button>
        </div>
      </div>
    </teleport>

    <div v-if="error && errorMessage" class="ui-datepicker__error">
      {{ errorMessage }}
    </div>
    <div v-else-if="helperText" class="ui-datepicker__helper">
      {{ helperText }}
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue';
import { UI_OVERLAY_DISMISS_EVENT } from '~/composables/useOverlayDismiss';

const { t, locale } = useI18n();

const props = defineProps({
  modelValue: {
    type: [Date, String, Array, null],
    default: null,
  },
  isRange: {
    type: Boolean,
    default: false,
  },
  splitRange: {
    type: Boolean,
    default: false,
  },
  label: {
    type: String,
    default: '',
  },
  placeholder: {
    type: String,
    default: '',
  },
  full: {
    type: Boolean,
    default: false,
  },
  startPlaceholder: {
    type: String,
    default: '',
  },
  endPlaceholder: {
    type: String,
    default: '',
  },
  format: {
    type: String,
    default: 'YYYY.MM.DD',
  },
  clearable: {
    type: Boolean,
    default: true,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  required: {
    type: Boolean,
    default: false,
  },
  error: {
    type: Boolean,
    default: false,
  },
  errorMessage: {
    type: String,
    default: '',
  },
  helperText: {
    type: String,
    default: '',
  },
  minDate: {
    type: [Date, String],
    default: null,
  },
  maxDate: {
    type: [Date, String],
    default: null,
  },
  size: {
    type: String,
    default: 'medium',
    validator: value => ['small', 'medium', 'large', 'xlarge'].includes(value),
  },
  block: {
    type: Boolean,
    default: false,
  },
  precision: {
    type: String,
    default: 'day',
    validator: value => ['year', 'month', 'day'].includes(value),
  },
});

const emit = defineEmits(['update:modelValue', 'change']);

// 상태 관리
const isOpen = ref(false);
const currentMonth = ref(new Date().getMonth());
const currentYear = ref(new Date().getFullYear());
const selectedDate = ref(null);
const startDate = ref(null);
const endDate = ref(null);
const tempStartDate = ref(null);
const tempEndDate = ref(null);
const viewMode = ref('days'); // 'years', 'months', 'days'
const activeField = ref(null);

// 월/년도 드롭다운 상태
const monthDropdownOpen = ref(false);
const yearDropdownOpen = ref(false);

// 달력 위치 관련
const rootRef = ref(null);
const inputRef = ref(null);
const calendarRef = ref(null);
const yearDropdownListRef = ref(null);
const calendarStyle = ref({});

const intlLocale = computed(() => (locale.value === 'ko' ? 'ko-KR' : 'en-US'));

const weekdays = computed(() => {
  locale.value;
  const formatter = new Intl.DateTimeFormat(intlLocale.value, { weekday: 'short' });
  return Array.from({ length: 7 }, (_, index) => formatter.format(new Date(2024, 0, 7 + index)));
});

const months = computed(() => {
  locale.value;
  const formatter = new Intl.DateTimeFormat(intlLocale.value, { month: 'long' });
  return Array.from({ length: 12 }, (_, index) => formatter.format(new Date(2024, index, 1)));
});

const yearTitle = computed(() => t('common.datePicker.yearLabel', { year: currentYear.value }));

// 년도 목록 생성 (현재 년도 기준 -10년 ~ +10년)
const yearRange = computed(() => {
  const yearList = [];
  const startYear = currentYear.value - 10;
  const endYear = currentYear.value + 10;

  for (let year = startYear; year <= endYear; year++) {
    yearList.push(year);
  }

  return yearList;
});

// 현재 월 이름
const currentMonthName = computed(() => months.value[currentMonth.value]);

// 캘린더 날짜 계산
const calendarDays = computed(() => {
  const days = [];

  // 월의 첫째 날과 마지막 날
  const firstDayOfMonth = new Date(currentYear.value, currentMonth.value, 1);
  const lastDayOfMonth = new Date(currentYear.value, currentMonth.value + 1, 0);

  // 첫째 날의 요일 (0 = 일요일)
  const firstDayWeekday = firstDayOfMonth.getDay();

  // 이전 달의 마지막 날
  const lastDayOfPrevMonth = new Date(currentYear.value, currentMonth.value, 0).getDate();

  // 이전 달의 날짜 추가
  for (let i = 0; i < firstDayWeekday; i++) {
    const date = new Date(currentYear.value, currentMonth.value - 1, lastDayOfPrevMonth - firstDayWeekday + i + 1);
    days.push({
      day: date.getDate(),
      date: date,
      currentMonth: false,
      disabled: isDateDisabled(date),
      selected: isSameDate(date, selectedDate.value),
      rangeStart: isSameDate(date, startDate.value),
      rangeEnd: isSameDate(date, endDate.value),
      inRange: isInRange(date),
    });
  }

  // 현재 달의 날짜 추가
  for (let i = 1; i <= lastDayOfMonth.getDate(); i++) {
    const date = new Date(currentYear.value, currentMonth.value, i);
    days.push({
      day: i,
      date: date,
      currentMonth: true,
      disabled: isDateDisabled(date),
      selected: isSameDate(date, selectedDate.value),
      rangeStart: isSameDate(date, startDate.value),
      rangeEnd: isSameDate(date, endDate.value),
      inRange: isInRange(date),
    });
  }

  // 다음 달의 날짜 추가 (6주를 채우기 위해)
  const remainingDays = 42 - days.length;
  for (let i = 1; i <= remainingDays; i++) {
    const date = new Date(currentYear.value, currentMonth.value + 1, i);
    days.push({
      day: i,
      date: date,
      currentMonth: false,
      disabled: isDateDisabled(date),
      selected: isSameDate(date, selectedDate.value),
      rangeStart: isSameDate(date, startDate.value),
      rangeEnd: isSameDate(date, endDate.value),
      inRange: isInRange(date),
    });
  }

  return days;
});

// 윈도우 리사이즈 핸들러
function handleResize() {
  if (isOpen.value) {
    calculateCalendarPosition();
  }
}

// 초기화
onMounted(() => {
  initializeValues();
  document.addEventListener('click', handleClickOutside);
  window.addEventListener(UI_OVERLAY_DISMISS_EVENT, handleOverlayDismiss);
  window.addEventListener('resize', handleResize);
  window.addEventListener('scroll', calculateCalendarPosition);

  // precision에 따라 초기 viewMode 설정
  if (props.precision === 'year') {
    viewMode.value = 'years';
  } else if (props.precision === 'month') {
    viewMode.value = 'months';
  }
});

// 언마운트시 이벤트 리스너 제거
onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
  window.removeEventListener(UI_OVERLAY_DISMISS_EVENT, handleOverlayDismiss);
  window.removeEventListener('resize', handleResize);
  window.removeEventListener('scroll', calculateCalendarPosition);
});

// 초기 값 설정
function initializeValues() {
  if (props.modelValue) {
    if (props.isRange && Array.isArray(props.modelValue)) {
      // 범위 모드일 때 배열로 받음 [시작일, 종료일]
      if (props.modelValue[0]) {
        startDate.value = new Date(props.modelValue[0]);
      }
      if (props.modelValue[1]) {
        endDate.value = new Date(props.modelValue[1]);
      }

      // 설정된 날짜 기준으로 캘린더 월/년 설정
      if (startDate.value) {
        currentMonth.value = startDate.value.getMonth();
        currentYear.value = startDate.value.getFullYear();
      }
    } else if (!props.isRange) {
      // 단일 날짜 모드
      try {
        selectedDate.value = new Date(props.modelValue);

        if (!isNaN(selectedDate.value.getTime())) {
          currentMonth.value = selectedDate.value.getMonth();
          currentYear.value = selectedDate.value.getFullYear();
        } else {
          selectedDate.value = null;
        }
      } catch (e) {
        selectedDate.value = null;
      }
    }
  }
}

// 월/년도 드롭다운 토글 메서드
function toggleMonthDropdown() {
  monthDropdownOpen.value = !monthDropdownOpen.value;
  if (monthDropdownOpen.value) {
    yearDropdownOpen.value = false;
  }
}

function scrollYearDropdownToSelected() {
  nextTick(() => {
    const list = yearDropdownListRef.value;
    if (!list) return;
    const selected = list.querySelector('.ui-datepicker__dropdown-item--selected');
    selected?.scrollIntoView({ block: 'center' });
  });
}

function toggleYearDropdown() {
  yearDropdownOpen.value = !yearDropdownOpen.value;
  if (yearDropdownOpen.value) {
    monthDropdownOpen.value = false;
    scrollYearDropdownToSelected();
  }
}

// 모든 드롭다운 닫기
function closeAllDropdowns() {
  monthDropdownOpen.value = false;
  yearDropdownOpen.value = false;
}

// 날짜 비교 유틸리티 함수
function isSameDate(date1, date2) {
  if (!date1 || !date2) return false;
  return date1.getDate() === date2.getDate() && date1.getMonth() === date2.getMonth() && date1.getFullYear() === date2.getFullYear();
}

// 날짜가 비활성화되었는지 확인
function isDateDisabled(date) {
  if (props.minDate) {
    const minDate = new Date(props.minDate);
    if (date < minDate) return true;
  }

  if (props.maxDate) {
    const maxDate = new Date(props.maxDate);
    if (date > maxDate) return true;
  }

  return false;
}

// 날짜가 선택되었는지 확인
function isSelected(date) {
  if (!date) return false;

  if (props.isRange) {
    // 범위 선택 모드에서는 시작일과 종료일 확인
    return (startDate.value && isSameDate(date, startDate.value)) || (endDate.value && isSameDate(date, endDate.value));
  } else {
    // 단일 선택 모드에서는 선택된 날짜와 비교
    return selectedDate.value && isSameDate(date, selectedDate.value);
  }
}

// 날짜가 범위 내에 있는지 확인
function isInRange(date) {
  if (!props.isRange || !startDate.value || !endDate.value || !date) return false;

  return date > startDate.value && date < endDate.value;
}

// 날짜 포맷팅
function formatDate(date) {
  if (!date || isNaN(date.getTime())) {
    return '';
  }

  try {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    let formattedDate = props.format;
    formattedDate = formattedDate.replace('YYYY', year);
    formattedDate = formattedDate.replace('MM', month);

    // precision="month"가 아닐 때만 DD 처리
    if (props.precision !== 'month') {
      formattedDate = formattedDate.replace('DD', day);
    }

    return formattedDate;
  } catch (e) {
    return '';
  }
}

// 달력 위치 계산
// 위로 flip 시 top 추정(400px) 대신 bottom 앵커로 입력 상단에 붙임 (UiSelect와 동일 패턴)
function calculateCalendarPosition() {
  if (!inputRef.value) return;

  const inputRect = inputRef.value.getBoundingClientRect();
  const calendarWidth = 360;
  const calendarHeight = 400; // flip 여부 판정용 (대략 높이)
  const spacing = 4;

  let left = inputRect.left;

  // 화면 우측 경계 체크
  if (left + calendarWidth > window.innerWidth) {
    left = window.innerWidth - calendarWidth - 10;
  }

  const spaceAbove = inputRect.top - spacing;
  const openBelow = inputRect.bottom + spacing;

  // 기본: 입력 아래. 아래 공간 부족 시 위(bottom 앵커) 또는 중앙(top)
  if (openBelow + calendarHeight > window.innerHeight && spaceAbove >= calendarHeight) {
    calendarStyle.value = {
      position: 'fixed',
      top: 'auto',
      bottom: `${window.innerHeight - inputRect.top + spacing}px`,
      left: `${left}px`,
      zIndex: '999999',
    };
    return;
  }

  let top = openBelow;
  if (openBelow + calendarHeight > window.innerHeight) {
    // 위아래 모두 공간 부족 → 화면 중앙
    top = Math.max(10, (window.innerHeight - calendarHeight) / 2);
  }

  calendarStyle.value = {
    position: 'fixed',
    top: `${top}px`,
    bottom: 'auto',
    left: `${left}px`,
    zIndex: '999999',
  };
}

// 캘린더에 표시할 기준 날짜 (splitRange: 클릭한 필드 기준)
function resolveCalendarAnchorDate() {
  if (!props.isRange) {
    return selectedDate.value || new Date();
  }

  if (props.splitRange && activeField.value === 'end') {
    return endDate.value || startDate.value || new Date();
  }

  if (props.splitRange && activeField.value === 'start') {
    return startDate.value || endDate.value || new Date();
  }

  return startDate.value || endDate.value || new Date();
}

function syncCalendarView(dateAnchor) {
  const date = dateAnchor || new Date();
  if (!isNaN(date.getTime())) {
    currentMonth.value = date.getMonth();
    currentYear.value = date.getFullYear();
  }
}

// 캘린더 열기
function openCalendar(se) {
  if (props.disabled) return;
  activeField.value = se;
  isOpen.value = true;

  // precision에 따라 초기 viewMode 설정
  if (props.precision === 'month') {
    viewMode.value = 'months';
  } else {
    viewMode.value = 'days';
  }

  if (props.isRange) {
    tempStartDate.value = startDate.value ? new Date(startDate.value) : null;
    tempEndDate.value = endDate.value ? new Date(endDate.value) : null;
  }

  syncCalendarView(resolveCalendarAnchorDate());

  // 다음 틱에서 위치 계산 (DOM 업데이트 후)
  nextTick(() => {
    calculateCalendarPosition();
  });
}

// 외부 클릭 처리 (다른 UiDatePicker 클릭 시에도 이 인스턴스는 닫힘 — closest('.ui-datepicker')만으로는 구분 불가)
function handleClickOutside(event) {
  if (!isOpen.value) return;

  const inThisPicker = rootRef.value?.contains(event.target);
  const inThisCalendar = calendarRef.value?.contains(event.target);

  if (inThisPicker || inThisCalendar) {
    return;
  }

  if (props.isRange) {
    startDate.value = tempStartDate.value;
    endDate.value = tempEndDate.value;
  }
  isOpen.value = false;
  closeAllDropdowns();
}

function handleOverlayDismiss() {
  if (!isOpen.value) return;

  if (props.isRange) {
    startDate.value = tempStartDate.value;
    endDate.value = tempEndDate.value;
  }
  isOpen.value = false;
  closeAllDropdowns();
}

// 이전/다음 월 이동
function prevMonth() {
  if (currentMonth.value === 0) {
    currentMonth.value = 11;
    currentYear.value--;
  } else {
    currentMonth.value--;
  }
  closeAllDropdowns();
}

function nextMonth() {
  if (currentMonth.value === 11) {
    currentMonth.value = 0;
    currentYear.value++;
  } else {
    currentMonth.value++;
  }
  closeAllDropdowns();
}

// 이전/다음 년도 이동
function prevYear() {
  currentYear.value--;
  closeAllDropdowns();
}

function nextYear() {
  currentYear.value++;
  closeAllDropdowns();
}

// 월 선택
function selectMonth(monthIndex) {
  if (props.precision === 'month') {
    const selectedMonthDate = new Date(currentYear.value, monthIndex, 1);

    if (props.isRange) {
      if (props.splitRange) {
        if (activeField.value === 'start') {
          startDate.value = selectedMonthDate;
          if (endDate.value && startDate.value > endDate.value) {
            alert(t('common.datePicker.startAfterEnd'));
            startDate.value = null;
            return;
          }
          const formattedStart = formatDate(startDate.value);
          const formattedEnd = endDate.value ? formatDate(endDate.value) : null;
          emit('update:modelValue', [formattedStart, formattedEnd]);
          emit('change', [formattedStart, formattedEnd]);
          isOpen.value = false;
          return;
        }
        if (activeField.value === 'end') {
          endDate.value = selectedMonthDate;
          if (startDate.value && endDate.value < startDate.value) {
            alert(t('common.datePicker.endBeforeStart'));
            endDate.value = null;
            return;
          }
          const formattedStart = startDate.value ? formatDate(startDate.value) : null;
          const formattedEnd = formatDate(endDate.value);
          emit('update:modelValue', [formattedStart, formattedEnd]);
          emit('change', [formattedStart, formattedEnd]);
          isOpen.value = false;
          return;
        }
        return;
      }
      // 범위 선택 모드에서 월 선택
      if (!startDate.value || (startDate.value && endDate.value)) {
        // 시작월이 없거나, 이미 범위가 선택된 경우 => 시작월로 설정
        startDate.value = selectedMonthDate;
        endDate.value = null;
      } else {
        // 시작월 이후의 월만 종료월로 선택 가능
        if (selectedMonthDate >= startDate.value) {
          endDate.value = selectedMonthDate;
        } else {
          // 시작월보다 이전 월을 선택한 경우, 시작월을 변경
          const temp = startDate.value;
          startDate.value = selectedMonthDate;
          endDate.value = temp;
        }
      }
    } else {
      // 단일 선택 모드
      selectedDate.value = selectedMonthDate;

      // props.format을 사용해서 포맷팅
      const formattedValue = formatDate(selectedMonthDate);
      emit('update:modelValue', formattedValue);
      emit('change', formattedValue);
      isOpen.value = false;
    }
  } else {
    // 기존 로직 (월 네비게이션)
    currentMonth.value = monthIndex;
    monthDropdownOpen.value = false;
  }
}

// 년도 선택
function selectYear(year) {
  currentYear.value = year;
  yearDropdownOpen.value = false;
}

// 날짜 선택
function selectDate(day) {
  if (day.disabled) return;
  closeAllDropdowns();

  if (props.isRange && !props.splitRange) {
    // 범위 선택 모드
    if (!startDate.value || (startDate.value && endDate.value)) {
      // 시작일이 없거나, 이미 범위가 선택된 경우 => 시작일로 설정
      startDate.value = new Date(day.date);
      endDate.value = null;
    } else {
      // 시작일 이후의 날짜만 종료일로 선택 가능
      if (day.date >= startDate.value) {
        endDate.value = new Date(day.date);
        // 자동 적용 (옵션)
        applySelection();
      } else {
        // 시작일보다 이전 날짜를 선택한 경우, 시작일을 변경
        const temp = new Date(startDate.value);
        startDate.value = new Date(day.date);
        endDate.value = temp;
        // 자동 적용 (옵션)
        applySelection();
      }
    }
    // 범위 날짜 선택 => 시작일/종료일 각각 클릭 가능한 경우
  } else if (props.isRange && props.splitRange) {
    // 시작일 선택
    if (activeField.value === 'start') {
      startDate.value = new Date(day.date);

      // 종료일보다 늦은 시작일은 불가능
      if (endDate.value && startDate.value > endDate.value) {
        alert(t('common.datePicker.startAfterEnd'));
        startDate.value = null;
        return;
      }

      const formattedStart = formatDate(startDate.value);
      const formattedEnd = endDate.value ? formatDate(endDate.value) : null;
      emit('update:modelValue', [formattedStart, formattedEnd]);
      emit('change', [formattedStart, formattedEnd]);
      isOpen.value = false;

      // 종료일 선택
    } else {
      endDate.value = new Date(day.date);

      // 시작일보다 이전 종료일은 불가능
      if (startDate.value && endDate.value < startDate.value) {
        alert(t('common.datePicker.endBeforeStart'));
        endDate.value = null;
        return;
      }
      const formattedStart = startDate.value ? formatDate(startDate.value) : null;
      const formattedEnd = formatDate(endDate.value);
      emit('update:modelValue', [formattedStart, formattedEnd]);
      emit('change', [formattedStart, formattedEnd]);
      isOpen.value = false;
    }
  } else {
    // 단일 선택 모드
    selectedDate.value = new Date(day.date);

    // 선택 즉시 적용하고 닫기
    emit('update:modelValue', selectedDate.value);
    emit('change', selectedDate.value);
    isOpen.value = false;
  }
}

// 날짜 지우기
function clearDate(event) {
  event.stopPropagation();

  if (props.isRange) {
    startDate.value = null;
    endDate.value = null;
    emit('update:modelValue', [null, null]);
    emit('change', [null, null]);
  } else {
    selectedDate.value = null;
    emit('update:modelValue', null);
    emit('change', null);
  }
}

// 시작일만 지우기
function clearStartDate(event) {
  event.stopPropagation();
  startDate.value = null;
  const formattedEnd = endDate.value ? formatDate(endDate.value) : null;
  emit('update:modelValue', [null, formattedEnd]);
  emit('change', [null, formattedEnd]);
}

// 종료일만 지우기
function clearEndDate(event) {
  event.stopPropagation();
  endDate.value = null;
  const formattedStart = startDate.value ? formatDate(startDate.value) : null;
  emit('update:modelValue', [formattedStart, null]);
  emit('change', [formattedStart, null]);
}

// 범위 선택 취소
function cancelSelection() {
  startDate.value = tempStartDate.value;
  endDate.value = tempEndDate.value;
  isOpen.value = false;
  closeAllDropdowns();
}

// 범위 선택 적용
function applySelection() {
  if (startDate.value) {
    const formattedStart = formatDate(startDate.value);
    const formattedEnd = endDate.value ? formatDate(endDate.value) : null;
    emit('update:modelValue', [formattedStart, formattedEnd]);
    emit('change', [formattedStart, formattedEnd]);
    isOpen.value = false;
    closeAllDropdowns();
  }
}

// 월이 선택되었는지 확인
function isMonthSelected(monthIndex) {
  if (props.isRange) {
    // 범위 모드에서는 시작월과 종료월 확인
    const isStartMonth = startDate.value && startDate.value.getMonth() === monthIndex && startDate.value.getFullYear() === currentYear.value;

    const isEndMonth = endDate.value && endDate.value.getMonth() === monthIndex && endDate.value.getFullYear() === currentYear.value;

    return isStartMonth || isEndMonth;
  } else {
    // 단일 모드에서는 선택된 월과 비교
    return selectedDate.value && selectedDate.value.getMonth() === monthIndex && selectedDate.value.getFullYear() === currentYear.value;
  }
}

// 월이 비활성화되었는지 확인
function isMonthDisabled(monthIndex) {
  const monthDate = new Date(currentYear.value, monthIndex, 1);

  if (props.minDate) {
    const minDate = new Date(props.minDate);
    const minMonth = new Date(minDate.getFullYear(), minDate.getMonth(), 1);
    if (monthDate < minMonth) return true;
  }

  if (props.maxDate) {
    const maxDate = new Date(props.maxDate);
    const maxMonth = new Date(maxDate.getFullYear(), maxDate.getMonth(), 1);
    if (monthDate > maxMonth) return true;
  }

  return false;
}

// 모델 값 변경 감시
watch(
  () => props.modelValue,
  newValue => {
    if (newValue === null || newValue === undefined) {
      selectedDate.value = null;
      startDate.value = null;
      endDate.value = null;
      return;
    }

    if (props.isRange && Array.isArray(newValue)) {
      startDate.value = newValue[0] ? new Date(newValue[0]) : null;
      endDate.value = newValue[1] ? new Date(newValue[1]) : null;
    } else if (!props.isRange) {
      try {
        selectedDate.value = newValue ? new Date(newValue) : null;
      } catch (e) {
        selectedDate.value = null;
      }
    }
  },
  { immediate: true },
);

// 선택한 날짜가 변경될 때마다 캘린더 업데이트
watch([selectedDate, startDate, endDate], () => {
  // splitRange + 캘린더 열림: 클릭한 필드(시작/종료) 기준 월 유지
  if (isOpen.value && props.isRange && props.splitRange && activeField.value) {
    syncCalendarView(resolveCalendarAnchorDate());
    return;
  }

  const dateToUse = props.isRange ? startDate.value || endDate.value : selectedDate.value;

  if (dateToUse) {
    syncCalendarView(dateToUse);
  }
});
</script>

<style lang="scss" scoped>
.ui-datepicker {
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;

  // 라벨
  &__label {
    font-size: 14px;
    font-weight: 500;
    color: var(--color-gray-70, #464c53);
    margin-bottom: 4px;
  }

  &__required {
    color: var(--color-system-r30, #f30);
    margin-left: 2px;
  }

  // 입력 영역
  &__wrapper {
    position: relative;
  }

  &__input,
  &__range-input {
    display: flex;
    align-items: center;
    height: 36px;
    padding: 8px 12px;
    border: 1px solid var(--color-gray-30, #b1b8be);
    border-radius: 4px;
    background: var(--color-gray-0, #fff);
    cursor: pointer;
    transition: border-color 0.2s ease;

    &:hover {
      border-color: var(--color-gray-50, #6e7781);
    }
  }

  &__icon {
    color: var(--color-gray-40, #8a949e);
    margin-right: 8px;
    flex-shrink: 0;
  }

  &__text,
  &__range-text {
    display: flex;
    align-items: center;
    gap: 4px;
    min-width: 0;
    color: var(--color-gray-70, #464c53);
    font-size: 14px;
  }

  &__placeholder {
    color: var(--color-gray-40, #8a949e);
  }

  &__range-separator {
    margin: 0 8px;
    color: var(--color-gray-40, #8a949e);
  }

  &__clear {
    margin-left: 8px;
    cursor: pointer;
    color: var(--color-gray-40, #8a949e);
    flex-shrink: 0;

    &:hover {
      color: var(--color-gray-70, #464c53);
    }

    &--inline {
      margin-left: 4px;
      margin-right: 0;
    }
  }

  // 캘린더
  &__calendar {
    position: absolute;
    top: calc(100% + 4px);
    left: 0;
    z-index: 100;
    width: 360px;
    background: var(--color-gray-0, #fff);
    border: 1px solid var(--color-gray-20, #cdd1d5);
    border-radius: 6px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    overflow: hidden;

    &--fixed {
      position: fixed;
      top: auto;
      left: auto;
      z-index: 999999;
    }
  }

  // 헤더
  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px;
    border-bottom: 1px solid var(--color-gray-20, #cdd1d5);
  }

  &__title {
    font-size: 16px;
    font-weight: 600;
    color: var(--color-gray-70, #464c53);
  }

  &__nav-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border: none;
    background: transparent;
    border-radius: 4px;
    cursor: pointer;
    color: var(--color-gray-60, #58616a);

    &:hover {
      background: var(--color-gray-10, #e6e8ea);
    }
  }

  // 선택기
  &__selectors {
    display: flex;
    gap: 8px;
  }

  &__dropdown {
    position: relative;
  }

  &__selector {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 4px 8px;
    border-radius: 4px;
    cursor: pointer;
    font-weight: 500;
    color: var(--color-gray-70, #464c53);

    &:hover {
      background: var(--color-gray-10, #e6e8ea);
    }
  }

  &__dropdown-list {
    position: absolute;
    top: 100%;
    left: 0;
    min-width: 100px;
    max-height: 200px;
    overflow-y: auto;
    background: var(--color-gray-0, #fff);
    border: 1px solid var(--color-gray-20, #cdd1d5);
    border-radius: 4px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    z-index: 10;
  }

  &__dropdown-item {
    padding: 6px 12px;
    cursor: pointer;
    color: var(--color-gray-70, #464c53);

    &:hover {
      background: var(--color-gray-10, #e6e8ea);
    }

    &--selected {
      background: var(--color-system-b10, #e6f4ff);
      color: var(--color-system-b30, #0084ff);
      font-weight: 500;
    }
  }

  // 월 선택 영역
  &__months {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;
    padding: 16px;
  }

  &__month {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 48px;
    border-radius: 8px;
    cursor: pointer;
    font-size: 14px;
    font-weight: 500;
    color: var(--color-gray-70, #464c53);
    transition: all 0.2s ease;

    &:hover:not(&--disabled):not(&--selected) {
      background: var(--color-gray-10, #e6e8ea);
    }

    &--selected {
      background: var(--color-system-b30, #0084ff);
      color: var(--color-gray-0, #fff);
    }

    &--disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }
  }

  // 요일
  &__weekdays {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    padding: 8px 0;
    border-bottom: 1px solid var(--color-gray-20, #cdd1d5);
  }

  &__weekday {
    text-align: center;
    font-size: 12px;
    font-weight: 500;
    color: var(--color-gray-60, #58616a);
  }

  // 날짜
  &__days {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    padding: 8px;
  }

  &__day {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 36px; // 32px에서 36px로 증가
    width: 36px; // 32px에서 36px로 증가
    margin: 2px;
    border-radius: 50%;
    cursor: pointer;
    font-size: 14px;
    color: var(--color-gray-70, #464c53);

    &:hover:not(&--disabled):not(&--selected) {
      background: var(--color-gray-10, #e6e8ea);
    }

    &--other-month {
      color: var(--color-gray-30, #b1b8be);
    }

    &--today {
      font-weight: 500;
      border: 1px solid var(--color-gray-30, #b1b8be);
    }

    &--selected {
      background: var(--color-system-b30, #0084ff);
      color: var(--color-gray-0, #fff);
    }

    &--in-range {
      background: var(--color-system-b10, #e6f4ff);
      border-radius: 0;
    }

    &--disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }
  }

  // 액션 버튼
  &__actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    padding: 12px;
    border-top: 1px solid var(--color-gray-20, #cdd1d5);
  }

  &__action-btn {
    padding: 6px 12px;
    border: none;
    border-radius: 4px;
    font-size: 14px;
    cursor: pointer;

    &--cancel {
      background: var(--color-gray-10, #e6e8ea);
      color: var(--color-gray-70, #464c53);

      &:hover {
        background: var(--color-gray-20, #cdd1d5);
      }
    }

    &--apply {
      background: var(--color-system-b30, #0084ff);
      color: var(--color-gray-0, #fff);

      &:hover {
        background: var(--color-system-b40, #0068cc);
      }

      &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
      }
    }
  }

  // 에러/헬퍼 텍스트
  &__error {
    font-size: 12px;
    color: var(--color-system-r30, #f30);
    margin-top: 2px;
  }

  &__helper {
    font-size: 12px;
    color: var(--color-gray-60, #58616a);
    margin-top: 2px;
  }

  // 상태별 스타일
  &--has-value {
    .ui-datepicker__input,
    .ui-datepicker__range-input {
      border-color: var(--color-gray-50, #6e7781);
    }
  }

  &--disabled {
    .ui-datepicker__input,
    .ui-datepicker__range-input {
      border-color: var(--color-gray-20, #cdd1d5);
      background: var(--color-gray-10, #e6e8ea);
      color: var(--color-gray-30, #b1b8be);
      cursor: not-allowed;
    }

    .ui-datepicker__text,
    .ui-datepicker__range-text,
    .ui-datepicker__placeholder,
    .ui-datepicker__icon {
      color: var(--color-gray-30, #b1b8be);
    }
  }

  &--error {
    .ui-datepicker__input,
    .ui-datepicker__range-input {
      border-color: var(--color-system-r30, #f30);
    }
  }

  // 크기 변형
  &--small {
    .ui-datepicker__input,
    .ui-datepicker__range-input {
      height: 32px;
      padding: 6px 8px 6px 10px;
    }
  }

  &--large {
    .ui-datepicker__input,
    .ui-datepicker__range-input {
      height: 48px;
      padding: 12px 16px;
    }
  }

  &--xlarge {
    .ui-datepicker__input,
    .ui-datepicker__range-input {
      height: 56px;
      padding: 16px 20px;
    }
  }
}
</style>
